import { Children } from 'react';
import type { ReactNode } from 'react';

import { Grid, Button, Box, useTheme } from '@simplywallst/ui-core';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import type { BoxProps } from '@simplywallst/ui-core';

import Carousel, { Slide, Slides } from '@components/Carousel';

import { StyledBox, StyledGrid } from './styled';

type Props = {
  children: ReactNode;
  image: string;
};

export const OnboardingSlide = ({ children, image }: Props) => {
  return (
    <Slide>
      <Grid templateColumnsMd="1fr 1fr" templateRows="auto">
        <Grid
          templateColumns="290px"
          placeContent="center"
          gap="16px"
          paddingTop="x3"
          paddingTopMd="x0"
        >
          {children}
        </Grid>
        <Grid alignContent="end">
          <img src={image} />
        </Grid>
      </Grid>
    </Slide>
  );
};

export type OnboardingSlideShowProps = {
  children: ReactNode[];
  footer?: ReactNode;
  backgroundColor?: BoxProps['backgroundColor'];
  dismissOnboardingSlideshow?: () => void;
};

export const OnboardingSlideShow = ({
  children,
  footer,
  backgroundColor = 'noir',
  dismissOnboardingSlideshow,
}: OnboardingSlideShowProps) => {
  const theme = useTheme();

  const childrenCount = Children.count(children);

  return (
    <Box
      overflow="hidden"
      position="relative"
      data-cy-id="onboarding-slideshow"
    >
      {dismissOnboardingSlideshow ? (
        <Box
          zIndex={1}
          position="absolute"
          top="8px"
          right="8px"
          backgroundColor="phantom"
          borderRadius="x6"
          overflow="hidden"
        >
          <Button
            styleType="passive"
            onClick={dismissOnboardingSlideshow}
            data-cy-id="dismiss-slideshow-button"
          >
            <CrossSolid
              fill={theme.color.white}
              opacity="0.5"
              aria-label="Dismiss slideshow"
            />
          </Button>
        </Box>
      ) : null}
      <Grid
        gap={footer !== undefined ? '56px' : '0px'}
        gapMd="20px"
        templateColumns="100%"
        templateRowsLg="84% auto"
        backgroundColor={backgroundColor}
        borderRadiusLg="x1"
        padding="x2"
        paddingTopLg="x3"
        paddingBottomLg="x3"
        paddingRightLg="x3"
        paddingLeftLg="x3"
        minHeight="100%"
      >
        <StyledGrid
          templateColumns="100%"
          templateRows="auto"
          borderRadius="x1"
          gap="16px"
          justifyContent="start"
        >
          <StyledBox footer={footer !== undefined} slides={childrenCount}>
            <Carousel
              autoPlay
              autoPlaySpeed={10000}
              showArrows
              showDots
              align="start"
              draggable
              totalSlides={childrenCount}
            >
              <Slides>{children}</Slides>
            </Carousel>
          </StyledBox>
        </StyledGrid>
        {footer ? footer : <Box height="56px" />}
      </Grid>
    </Box>
  );
};
